// React
import { useEffect, useState } from 'react';

// Store
import useAuthStore from '../store/authStore.js';
import useProfileStore from '../store/profileStore.js';
import useSiteStore from '../store/siteStore.js';

// Token Management
import { renewToken, getTokenTimeLeft } from '../service/AuthService.js';

// Components
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import axios from 'axios';

// Page
import Dashboard from './Dashboard/index.js';
import Registration from './Registration/index.js';

// Styles
import { darkTheme } from '../styles/colorSchemes/index.js';
import { blue, orange } from '@mui/material/colors/index.js';

function App() {
	const { initAuth, isLoggedIn, waitForLogin, setSessionTimeLeft } = useAuthStore();
	const { fetchProfileData } = useProfileStore();
	const darkMode = useSiteStore(state => state.darkMode);
	const [sessionExpired, setSessionExpired] = useState(false);
	const [showWarning, setShowWarning] = useState(false);

	const primaryColor = darkMode ? orange[500] : blue[500];
	const primaryHoverColor = darkMode ? orange[700] : blue[700];

	const getTokenRemainingTime = () => {
		const token = localStorage.getItem('token');
		return getTokenTimeLeft(token);
	};

	useEffect(() => {
		initAuth();
	}, [initAuth])

	useEffect(() => {
		if (isLoggedIn) fetchProfileData();
	}, [isLoggedIn, fetchProfileData]);

	// Apply dark-mode class to body for global CSS styling (scrollbars, etc.)
	useEffect(() => {
		if (darkMode) {
			document.body.classList.add('dark-mode');
		} else {
			document.body.classList.remove('dark-mode');
		}
	}, [darkMode]);

	// Set up Axios interceptor ONCE
	useEffect(() => {
		const interceptor = axios.interceptors.response.use(
			response => response,
			error => {
				if (error.response && error.response.status === 401) {
					setSessionExpired(true);
				}
				return Promise.reject(error);
			}
		);
		return () => axios.interceptors.response.eject(interceptor);
	}, []);

	// Session timer
	useEffect(() => {
		if (isLoggedIn) {
			setSessionTimeLeft(getTokenRemainingTime());
			const interval = setInterval(() => {
				setSessionTimeLeft(sessionTimeLeft => {
					if (sessionTimeLeft === 0) {
						setSessionExpired(true);
						return 0;
					}
					if (sessionTimeLeft === 1 * 60 && !showWarning) {
						setShowWarning(true);
					}
					return Math.max(0, sessionTimeLeft - 1);
				});
			}, 1000);
			return () => clearInterval(interval);
		} else {
			setShowWarning(false);
		}
	}, [isLoggedIn, showWarning, setSessionTimeLeft]);

	const handleReload = () => {
		window.location.reload();
	};

	const handleCloseWarning = () => {
		setShowWarning(false);
	};

	const handleRenewSession = async () => {
		const success = await renewToken();
		if (success) {
			setShowWarning(false);
			setSessionTimeLeft(getTokenRemainingTime());
		}
	};


	return (
		<Box sx={{ flexGrow: 1 }}>
			{(!waitForLogin) && (
				isLoggedIn ? <Dashboard /> : <Registration />
			)}

			{/* Dialog: Sitzung abgelaufen */}
			<Dialog
				open={sessionExpired}
				PaperProps={{
					sx: {
						bgcolor: darkMode ? darkTheme.cardBg : 'white',
						color: darkMode ? darkTheme.textPrimary : 'inherit',
						borderRadius: 2
					}
				}}
			>
				<DialogTitle sx={{ color: darkMode ? darkTheme.textPrimary : 'inherit' }}>
					Sitzung abgelaufen
				</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ color: darkMode ? darkTheme.textSecondary : 'text.secondary' }}>
						Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.
					</DialogContentText>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
						<Button
							onClick={handleReload}
							variant="contained"
							sx={{
								bgcolor: primaryColor,
								'&:hover': { bgcolor: primaryHoverColor }
							}}
						>
							Neu laden
						</Button>
					</Box>
				</DialogContent>
			</Dialog>

			{/* Dialog: Sitzung läuft bald ab */}
			<Dialog
				open={showWarning && isLoggedIn}
				PaperProps={{
					sx: {
						bgcolor: darkMode ? darkTheme.cardBg : 'white',
						color: darkMode ? darkTheme.textPrimary : 'inherit',
						borderRadius: 2
					}
				}}
			>
				<DialogTitle sx={{ color: darkMode ? darkTheme.textPrimary : 'inherit' }}>
					Sitzung läuft ab
				</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ color: darkMode ? darkTheme.textSecondary : 'text.secondary' }}>
						Ihre Sitzung läuft in 1 Minute ab. Möchten Sie die Sitzung verlängern?
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button
						onClick={handleCloseWarning}
						variant="contained"
						sx={{
							bgcolor: primaryColor,
							'&:hover': { bgcolor: primaryHoverColor }
						}}
					>
						Ablehnen
					</Button>
					<Button
						onClick={handleRenewSession}
						variant="outlined"
						sx={{
							color: primaryColor,
							borderColor: primaryColor,
							'&:hover': {
								bgcolor: primaryHoverColor,
								borderColor: primaryHoverColor,
								color: 'white'
							}
						}}
					>
						Sitzung verlängern

					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default App;